import { tenantBootstrapSchema, type TenantBootstrapResponse } from '@unified-lk/api-contracts'
import { dhcTenant } from './tenants/dhc'
import { ulybkaTenant } from './tenants/ulybka'

export type TenantId = 'dhc' | 'ulybka'
export type Role = 'patient' | 'staff'

export interface ClinicContact {
  legalName: string
  legalDetails: string
  address: string
  workHours?: string
  phone: string
  phoneTel: string
  email: string
  website: string
  websiteLabel: string
  whatsappUrl: string
  whatsappLabel?: string
  telegramUrl: string
  telegramLabel?: string
  telegramNotifyUrl?: string
  telegramNotifyLabel?: string
  maxBotUrl?: string
  maxBotLabel?: string
  vkUrl?: string
  youtubeUrl?: string
  publicLogoUrl: string
  publicAccentColor: string
  patientPortalUrl: string
  patientPortalLabel?: string
}

export interface TenantBootstrapModel {
  tenant: TenantId
  role: Role
  host: string
  branding: {
    appName: string
    shortName: string
    logoSrc: string | null
    primaryColor: string
    basePaths: {
      patient: string
      staff: string
    }
  }
  clinic: ClinicContact
  capabilities: Record<string, boolean>
  apiBase: string
  auth: {
    requestCodePath: string
    channelsPath: string
    verifyCodePath: string
    refreshPath: string
    verifyPath: string
    csrfPath: string
    logoutPath: string
  }
}

const tenants = {
  dhc: dhcTenant,
  ulybka: ulybkaTenant,
}

export function detectTenant(hostname?: string): TenantId {
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : 'dev-lk.dhcentre.local')).toLowerCase()
  return host.includes('ulybka') || host.includes('ulybkamsk') ? 'ulybka' : 'dhc'
}

export function resolveTenantBootstrap(role: Role, hostname?: string): TenantBootstrapModel {
  const tenant = detectTenant(hostname)
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : 'localhost')
  const config = tenants[tenant]
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const isDevHost = host.startsWith('dev-')
  // plans.* subdomain hosts only public share pages; URLs are short (/d/{token}, /public/{token})
  // and the API is proxied at root /api on this host. Force basePath="/" regardless of tenant.
  const isPublicPlansHost = host.toLowerCase().startsWith('plans.')
  // doc.* subdomains are dedicated doctor-portal hosts (e.g. doc.dhcentre.ru, doc.ulybkamsk.ru).
  // The app is served at root "/" so API lives at /api/, not /lk_doctor/api/.
  const isDocHost = /^doc\./.test(host.toLowerCase())
  const basePath = isDevHost || isPublicPlansHost || isDocHost ? '/' : deriveBasePath(config.branding.basePaths[role], role, pathname)
  const normalizedBase = basePath === '/' ? '' : basePath

  return {
    tenant,
    role,
    host,
    branding: config.branding,
    clinic: config.clinic,
    capabilities: { ...config.capabilities },
    apiBase: `${normalizedBase}/api`,
    auth: {
      requestCodePath: `${normalizedBase}/api/auth/request-code`,
      channelsPath: `${normalizedBase}/api/auth/channels`,
      verifyCodePath: `${normalizedBase}/api/auth/verify-code`,
      refreshPath: `${normalizedBase}/api/auth/refresh`,
      verifyPath: `${normalizedBase}/api/auth/verify`,
      csrfPath: `${normalizedBase}/api/auth/csrf`,
      logoutPath: `${normalizedBase}/api/auth/logout`,
    },
  }
}

function deriveBasePath(configuredBasePath: string, role: Role, pathname: string): string {
  if (configuredBasePath === '/') {
    return '/'
  }

  if (pathname.startsWith(`/${role === 'patient' ? 'lk_patient' : 'lk_doctor'}`)) {
    return `/${role === 'patient' ? 'lk_patient' : 'lk_doctor'}`
  }

  return configuredBasePath
}

export async function fetchTenantBootstrap(role: Role): Promise<TenantBootstrapModel> {
  const fallback = resolveTenantBootstrap(role)

  try {
    const response = await fetch(`${fallback.apiBase}/tenant/bootstrap`, {
      headers: {
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Bootstrap request failed with ${response.status}`)
    }

    const json = (await response.json()) as TenantBootstrapResponse
    const parsed = tenantBootstrapSchema.parse(json)

    return {
      tenant: parsed.tenant.tenant as TenantId,
      role: parsed.tenant.role,
      host: parsed.tenant.host,
      branding: parsed.tenant.branding,
      clinic: fallback.clinic,
      capabilities: parsed.tenant.capabilities,
      apiBase: fallback.apiBase,
      // Prefer locally-derived auth paths: the server builds them from basePaths which may
      // differ for dedicated-subdomain hosts (doc.*, dev-*) where the app is served at root.
      auth: fallback.auth,
    }
  } catch {
    return fallback
  }
}
